import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { MrpInventoryListResponse, MrpInventoryRow } from "@/lib/mrp";

import {
  aggregatePhysicalOnHandBySku,
  normalizeMrpSiteCode,
} from "./transfersOriginInventoryModel";

export {
  aggregatePhysicalOnHandBySku,
  buildTransferOriginOnHandLookup,
  formatTransferOriginOnHand,
  lookupOriginPhysicalOnHand,
  mrpSiteCodeForLocationId,
  normalizeMrpSiteCode,
  originOnHandExceedsQuantity,
  transferOrderHasOriginInventoryShortfall,
  TRANSFER_ORIGIN_ON_HAND_HOVER_HINT,
  type TransferOriginOnHandLookup,
} from "./transfersOriginInventoryModel";

function inventoryBySiteFromRows(
  siteCodes: string[],
  rows: MrpInventoryRow[],
): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>();
  for (const siteCode of siteCodes) {
    out.set(siteCode, new Map());
  }
  const rowsBySite = new Map<string, MrpInventoryRow[]>();
  for (const row of rows) {
    const siteCode = normalizeMrpSiteCode(row.location_code);
    if (siteCode == null || !out.has(siteCode)) continue;
    const list = rowsBySite.get(siteCode) ?? [];
    list.push(row);
    rowsBySite.set(siteCode, list);
  }
  for (const [siteCode, siteRows] of rowsBySite) {
    out.set(siteCode, aggregatePhysicalOnHandBySku(siteRows));
  }
  return out;
}

/**
 * Origin on-hand for transfer board lines: one inventory read for the needed sites + SKUs
 * (no per-site fan-out of full warehouse dumps).
 */
export function useTransferOriginInventory(
  siteCodes: string[],
  skus: string[],
): {
  inventoryBySite: Map<string, Map<string, number>>;
  isLoading: boolean;
} {
  const normalizedCodes = useMemo(
    () =>
      [
        ...new Set(
          siteCodes
            .map((code) => normalizeMrpSiteCode(code))
            .filter((code): code is string => code != null),
        ),
      ].sort(),
    [siteCodes],
  );

  const normalizedSkus = useMemo(
    () =>
      [
        ...new Set(skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0)),
      ].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" })),
    [skus],
  );
  const normalizedCodesKey = normalizedCodes.join(",");
  const normalizedSkusKey = normalizedSkus.join("\n");
  const [debouncedKeys, setDebouncedKeys] = useState({
    codes: normalizedCodesKey,
    skus: normalizedSkusKey,
  });
  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      setDebouncedKeys({ codes: normalizedCodesKey, skus: normalizedSkusKey });
    }, 300);
    return () => globalThis.clearTimeout(timer);
  }, [normalizedCodesKey, normalizedSkusKey]);
  const debouncedCodes = useMemo(
    () => debouncedKeys.codes.split(",").filter(Boolean),
    [debouncedKeys.codes],
  );
  const debouncedSkus = useMemo(
    () => debouncedKeys.skus.split("\n").filter(Boolean),
    [debouncedKeys.skus],
  );

  const inventoryQuery = useQuery<MrpInventoryListResponse>({
    queryKey: [
      "mrp",
      "inventory",
      "transfers-origin",
      debouncedKeys.codes,
      debouncedKeys.skus,
    ],
    queryFn: ({ signal }) =>
      apiFetch<MrpInventoryListResponse>("/api/mrp/inventory", {
        query: {
          location: debouncedKeys.codes,
          cpns: debouncedSkus.join(","),
          pick_route: true,
        },
        signal,
      }),
    enabled: debouncedCodes.length > 0 && debouncedSkus.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const inventoryBySite = useMemo(
    () => inventoryBySiteFromRows(debouncedCodes, inventoryQuery.data?.rows ?? []),
    [debouncedCodes, inventoryQuery.data?.rows],
  );

  return {
    inventoryBySite,
    isLoading: inventoryQuery.isLoading,
  };
}
